import { useTheme } from '@mui/styles';
import { muiTheme } from '@tassat/payments-themes';
import { Field } from 'formik';
import { merge } from 'lodash';
import * as React from 'react';

import createStyles from './styles';
import { RecipientFieldProps } from './types';
import ErrorMessage from '../ErrorMessage';
import muiStyles from '../fieldStyles';
import StyledSelect from '../StyledSelect';

export default function SendRecipientFieldComponent(props: RecipientFieldProps) {
    const {
        name,
        label,
        placeholder,
        addressBookOptions,
        components,
        walletAddressChanged,
        disabled,
        values,
        errors,
        customStyles = {}
    } = props;

    const theme = useTheme<typeof muiTheme>();
    const classes = muiStyles(props);
    const styles = createStyles(theme || muiTheme, props);
    const mergedStyles = React.useMemo(() => merge({}, styles, customStyles), [ styles, customStyles ]);

    const [ isMenuOpen, setIsMenuOpen ] = React.useState<boolean>(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const customComponents = React.useMemo((): {
        Option?: any;
        DropdownIndicator?: any;
        Input?: any;
    } => {
        const renderers = {} as {
            Option?: any;
            DropdownIndicator?: any;
            Input?: any;
        };

        if (components?.Option) {
            renderers.Option = components.Option;
        }

        if (components?.DropdownIndicator) {
            renderers.DropdownIndicator = components.DropdownIndicator;
        }

        if (components?.Input) {
            renderers.Input = components.Input;
        }

        return renderers;
    }, [components]);

    const handleAddressBookSelect = (field, form, selectedOption) => {
        if (!field.touched) {
            form.setFieldTouched('RecipientAddress', true);
        }

        if (isMenuOpen) setIsMenuOpen(false);

        form.setFieldValue('Recipient', selectedOption);
        form.setFieldValue('RecipientAddress', selectedOption.value.UIWalletAddress ?
            selectedOption.value.UIWalletAddress : selectedOption.value.Account);
    };

    const handleTextInputChange = (e, field, form) => {
        const currentValue = e.target.value;
        if (isMenuOpen) setIsMenuOpen(false);

        if (currentValue[0] === 'S') {
            e.target.value = `0x${currentValue.slice(1)}`;
        } else if (currentValue.slice(0, 2) !== '0x' && currentValue.length === 40) {
            e.target.value = `0x${currentValue}`;
        } else if (currentValue.slice(0, 2) !== '0x' && currentValue.length === 2) {
            e.target.value = `0x${currentValue}`;
        } else if (currentValue.slice(0, 2) !== '0x' && currentValue.length >= 2) {
            return;
        }

        if (!field.touched) {
            form.setFieldTouched('RecipientAddress', true);
        }

        if (values.Recipient) {
            form.setFieldValue('Recipient', null);
        }
        form.setFieldValue('RecipientAddress', e.target.value);
    };

    const handleFieldClear = (e, form) => {
        if (isMenuOpen) setIsMenuOpen(false);

        if ((e.key === 'Backspace' || e.key === 'Delete') && e.target.value.slice(0, 2) !== '0x') {
            e.target.value = '';
            form.setFieldValue('Recipient', null);
            form.setFieldValue('RecipientAddress', '');
        }
    };

    return (
        <Field
            name={name}
        >
            {({ field, form }) => (
                <div className={classes.field}>
                    <label
                        className={classes.inputLabel}
                        htmlFor={name}
                    >
                        {label}
                    </label>
                    <div className={classes.inputContainer}>
                        {
                            !customComponents.Input &&
                            <input
                                className={classes.textInput}
                                disabled={disabled}
                                maxLength={50}
                                name={name}
                                onBlur={() => {
                                    if (!field.touched) {
                                        form.setFieldTouched('RecipientAddress', true);
                                    }

                                    if (isMenuOpen) setIsMenuOpen(false);
                                }}
                                onChange={(e) => {
                                    walletAddressChanged();
                                    handleTextInputChange(e, field, form);
                                }}
                                onKeyDown={(e) => handleFieldClear(e, form)}
                                placeholder={placeholder}
                                ref={inputRef}
                                type='text'
                                value={values.Recipient ? values.Recipient.label : values.RecipientAddress}
                            />
                        }
                        <StyledSelect
                            components={{ ...customComponents }}
                            customStyles={mergedStyles}
                            disabled={disabled}
                            isSearchable={components?.Input ? true : false}
                            menuIsOpen={isMenuOpen}
                            onChange={(selectedOption) => handleAddressBookSelect(field, form, selectedOption)}
                            onMenuClose={() => setIsMenuOpen(false)}
                            onMenuOpen={() => {
                                if (inputRef.current) {
                                    inputRef.current.focus();
                                }
                                setIsMenuOpen(true);
                            }}
                            options={addressBookOptions}
                            placeholder='Select Recipient'
                            value={addressBookOptions.find((opt) => opt.value === values.Recipient?.value)}
                        />
                    </div>
                    <ErrorMessage
                        error={errors[field.name]}
                        field={field}
                        form={form}
                    />
                </div>
            )}
        </Field>
    );
}